/**
 * AckCache — cache idempotente de `client_ack` por `event_id`.
 *
 * Spec FE_CHANGES §5:
 *   "El ack DEBE ser idempotente — si el FE recibe el mismo event_id dos
 *    veces (replay), responde dos client_ack con el mismo payload."
 *
 * Implementación: LRU cap acotado (default 256) sobre el `event_id`.
 * No mantiene TTL — el BE solo replays dentro del log circular (segundos),
 * y el cap por count es suficiente para evitar growth indefinido.
 */

import { AckCacheEntry, ClientAckPayload } from './envelope.types';

export class AckCache {
  /** Cap de entradas. Cuando se supera, se trimmea a la mitad inferior. */
  private readonly capacity: number;

  /** Map ordenado de event_id → entry. Map preserva orden de inserción. */
  private readonly entries: Map<string, AckCacheEntry>;

  constructor(capacity: number = 256) {
    if (capacity <= 0) {
      throw new Error(`AckCache capacity must be > 0, got ${capacity}`);
    }
    this.capacity = capacity;
    this.entries = new Map();
  }

  /** Tamaño actual (para tests / debug). */
  get size(): number {
    return this.entries.size;
  }

  /**
   * Obtiene el ack cacheado (solo el payload wire) para un event_id, o
   * undefined si no existe.
   *
   * NO mueve la entrada al frente (LRU "access-order" no aplica acá):
   * el orden de inserción es suficiente porque siempre la primera vez se
   * llama `store` y luego solo `getOrUndefined` para replays.
   */
  getOrUndefined(eventId: string): ClientAckPayload | undefined {
    return this.entries.get(eventId)?.payload;
  }

  /**
   * Obtiene la entrada completa (payload + debugTs) para inspección/debug.
   */
  getEntryOrUndefined(eventId: string): AckCacheEntry | undefined {
    return this.entries.get(eventId);
  }

  /**
   * Cachea el payload para futuras llamadas con el mismo event_id.
   *
   * Si ya existía una entrada con ese event_id, sobreescribe (defensa contra
   * envíos repetidos durante el mismo render con un payload mutado).
   *
   * @param eventId discriminador idempotente del evento
   * @param payload shape EXACTO que se serializa al wire (sin debug ts)
   * @param debugTs timestamp local opcional para diagnóstico
   */
  store(eventId: string, payload: ClientAckPayload, debugTs: number = Date.now()): void {
    if (!eventId) {
      // Sin event_id no podemos cachear — ignorar silenciosamente. El caller
      // ya debería haber filtrado este caso, pero defendemos por las dudas.
      return;
    }
    // FE-4 — no cachear applied=false. Si un replay llega DESPUÉS de que el
    // mismo event fue aplicado con éxito (applied=true), el cache retornaría
    // applied=false eternamente y el BE lo re-aplicaría infinitamente.
    // Al no cachear fallos, el próximo replay recalcula desde cero.
    if (!payload.applied) {
      return;
    }
    this.entries.set(eventId, { payload, debugTs });
    if (this.entries.size > this.capacity) {
      this.trim();
    }
  }

  /** Limpieza para tests o teardown de sesión. */
  clear(): void {
    this.entries.clear();
  }

  /**
   * Trim a la mitad inferior cuando se supera la capacidad.
   *
   * Comportamiento: descarta el primer 50% (las entradas más antiguas).
   * Decisión: trim por batches (no por elemento) → amortizado O(1) por insert.
   */
  private trim(): void {
    const target = Math.floor(this.capacity / 2);
    const toDrop = this.entries.size - target;
    if (toDrop <= 0) return;
    let dropped = 0;
    for (const key of this.entries.keys()) {
      if (dropped >= toDrop) break;
      this.entries.delete(key);
      dropped++;
    }
  }
}
